"use strict";
export const DEFAULT_TITLE = "Topaz Dex";
export const DEFAULT_DESCRIPTION = "Topaz Dex \u2014 the ve(3,3) decentralized exchange on BNB Chain. Swap, provide liquidity, lock TOPAZ, and vote.";
const SWAP_DESCRIPTION = "Swap crypto on BNB Chain with the best rates on Topaz Dex \u2014 the ve(3,3) decentralized exchange.";
const LIQUIDITY_DESCRIPTION = "Provide liquidity on Topaz Dex to earn trading fees and TOPAZ emissions on BNB Chain.";
const STATIC_ROUTE_META = {
  // Must match the static tags in public/index.html — the raw file serves "/",
  // and client hydration (MetaData.tsx) re-renders this same value.
  "/": { title: "Topaz Dex \u2014 Swap on BNB Chain", description: DEFAULT_DESCRIPTION },
  "/swap": { title: "Swap | Topaz Dex", description: SWAP_DESCRIPTION },
  "/limit": {
    title: "Limit Orders | Topaz Dex",
    description: "Place limit orders on BNB Chain with Topaz Dex \u2014 set your price and trade automatically."
  },
  "/dca": {
    title: "DCA | Topaz Dex",
    description: "Dollar-cost average into any BNB Chain token automatically with Topaz Dex."
  },
  "/send": { title: "Send | Topaz Dex", description: "Send tokens on BNB Chain with Topaz Dex." },
  "/explore": {
    title: "Explore Tokens & Pools | Topaz Dex",
    description: "Explore top tokens and liquidity pools on BNB Chain \u2014 prices, volume, and TVL on Topaz Dex."
  },
  "/tokens": {
    title: "Explore Tokens | Topaz Dex",
    description: "Explore top tokens on BNB Chain \u2014 real-time prices and volume on Topaz Dex."
  },
  "/earn": { title: "Earn | Topaz Dex", description: LIQUIDITY_DESCRIPTION },
  "/liquidity": { title: "Liquidity Pools | Topaz Dex", description: LIQUIDITY_DESCRIPTION },
  "/positions": { title: "Your Positions | Topaz Dex", description: LIQUIDITY_DESCRIPTION },
  "/pool": { title: "Liquidity Pools | Topaz Dex", description: LIQUIDITY_DESCRIPTION },
  "/pools": { title: "Liquidity Pools | Topaz Dex", description: LIQUIDITY_DESCRIPTION },
  "/add": { title: "Add Liquidity | Topaz Dex", description: LIQUIDITY_DESCRIPTION },
  "/remove": { title: "Remove Liquidity | Topaz Dex", description: LIQUIDITY_DESCRIPTION },
  "/vote": {
    title: "Vote | Topaz Dex",
    description: "Vote with veTOPAZ to direct TOPAZ emissions and earn bribes and trading fees on Topaz Dex."
  },
  "/locks": {
    title: "Locks | Topaz Dex",
    description: "Lock TOPAZ for veTOPAZ to vote on emissions and share protocol rewards on Topaz Dex."
  },
  "/lock": {
    title: "Lock TOPAZ | Topaz Dex",
    description: "Lock TOPAZ for veTOPAZ to vote on emissions and share protocol rewards on Topaz Dex."
  },
  "/incentives": {
    title: "Incentives | Topaz Dex",
    description: "Deposit incentives to attract votes and liquidity to your pool on Topaz Dex."
  },
  "/relays": {
    title: "Relays | Topaz Dex",
    description: "Deposit veTOPAZ into a relay to earn rewards without managing a weekly vote on Topaz Dex."
  },
  "/portfolio": {
    title: "Portfolio | Topaz Dex",
    description: "Track your positions, locks, votes, and rewards on Topaz Dex."
  },
  // Redirects to /portfolio. Kept so a crawler or a shared link still gets the
  // right title card instead of the generic fallback before the hop.
  "/dashboard": {
    title: "Portfolio | Topaz Dex",
    description: "Track your positions, locks, votes, and rewards on Topaz Dex."
  },
  "/ai": {
    title: "Topaz Prizm | Topaz Dex",
    description: "Chat with Topaz Prizm, your on-chain DeFi strategist for swaps, liquidity and veTOPAZ on BNB Chain."
  },
  "/bridge": {
    title: "Bridge | Topaz Dex",
    description: "Bridge assets to and from BNB Chain with Topaz Dex."
  },
  "/privacy": { title: "Privacy Policy | Topaz Dex", description: DEFAULT_DESCRIPTION }
};
export function getStaticRouteMeta(pathname) {
  const normalized = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const exact = STATIC_ROUTE_META[normalized];
  if (exact) {
    return exact;
  }
  const firstSegment = "/" + (normalized.split("/")[1] ?? "");
  return STATIC_ROUTE_META[firstSegment] ?? { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION };
}
const ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;
function parseEntityPath(pathname, prefixes) {
  for (const prefix of prefixes) {
    if (!pathname.startsWith(prefix + "/")) {
      continue;
    }
    const rest = pathname.slice(prefix.length + 1).split("/");
    if (rest.length >= 2 && ADDRESS_REGEX.test(rest[1])) {
      return { chain: rest[0].toLowerCase(), address: rest[1].toLowerCase() };
    }
  }
  return void 0;
}
export function parseTokenPath(pathname) {
  return parseEntityPath(pathname, ["/explore/tokens", "/tokens"]);
}
export function parsePoolPath(pathname) {
  return parseEntityPath(pathname, ["/explore/pools"]);
}
export function formatUsdCompact(value) {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === void 0 || !isFinite(num) || num <= 0) {
    return void 0;
  }
  if (num >= 1e9) {
    return `$${(num / 1e9).toFixed(2)}B`;
  }
  if (num >= 1e6) {
    return `$${(num / 1e6).toFixed(2)}M`;
  }
  if (num >= 1e3) {
    return `$${(num / 1e3).toFixed(2)}K`;
  }
  if (num >= 0.01) {
    return `$${num.toFixed(2)}`;
  }
  return `$${num.toPrecision(3)}`;
}
export function formatFeePercent(feePips) {
  if (feePips === null || feePips === void 0 || !isFinite(feePips) || feePips < 0) {
    return void 0;
  }
  const percent = feePips / 1e4;
  const rounded = parseFloat(percent.toFixed(4));
  return `${rounded}%`;
}
